import { createContext, useContext, useState } from "react";
import { ConfirmQuestionUpdate } from "./Modal/ConfirmQuestionUpdate";

type ConfirmOptions = {
  hide?: boolean;
  delete?: boolean;
};

type ShowConfirmArgs = {
  title?: string;
  message?: React.ReactNode;
  options?: ConfirmOptions;
  onDelete?: () => void;
  onHide?: () => void;
  isHidden?: boolean;
};

type ConfirmContextType = {
  confirmDelete: (args: ShowConfirmArgs) => void;
};

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [modalArgs, setModalArgs] = useState<ShowConfirmArgs | null>(null);

  const confirmDelete = (args: ShowConfirmArgs) => {
    setModalArgs(args);
  };

  const handleClose = () => {
    setModalArgs(null);
  };

  const handleConfirm = () => {
    if (modalArgs?.onDelete) {
      modalArgs.onDelete();
    }
    setModalArgs(null);
  };

  const handleHide = () => {
    if (modalArgs?.onHide) {
      modalArgs.onHide();
    }
    setModalArgs(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirmDelete }}>
      {children}
      <ConfirmQuestionUpdate
        isOpen={!!modalArgs}
        onClose={handleClose}
        onConfirm={handleConfirm}
        onHide={handleHide}
        title={modalArgs?.title}
        message={modalArgs?.message}
        options={modalArgs?.options}
        isHidden={modalArgs?.isHidden}
      />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx.confirmDelete;
};
